/**
 * This slice is to manage the assets display view
 */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { assetService } from 'features/services';
import { publishById } from './assetManagerSlice';
import { LoadingStatus, SavingStatus } from 'GeminiViewerComponent/helpers/AsyncStatus';
import { deleteAsset, editAsset } from './editAssetSlice';
import { saveAs } from 'file-saver';

const initialState = {
    assets: [],
    allAssets: [],
    assetPageInfo: {},
    status: LoadingStatus.Idle,
    loadedRowsMap: {},

    error: null,
};

export const fetchAssetsPage = createAsyncThunk(
    'assets/fetchAssetsPage',
    async (params, { getState }) =>
        await assetService.getAll(
            params.page,
            params.pageSize,
            params.searchString,
            params.searchByAssetType,
            params.lastPublishedDate,
            params.sort,
            getState().accounts.activeUser.s3_prefix
        )
);
export const fetchAllAssets = createAsyncThunk(
    'assets/fetchAllAssets',
    async (params) => await assetService.getAllAsset(params.lastPublishedDate)
);

export const fetchMoreAssets = createAsyncThunk('assets/fetchMoreAssets', async (params, { getState }) => {
    return await assetService.getRange(
        params.startIndex,
        params.stopIndex,
        params.searchString,
        params.assetCategoryId,
        getState().accounts.activeUser.s3_prefix,
        params.searchByAssetType,
        params.lastPublishedDate,
        params.sort
    );
});

export const addNewAsset = createAsyncThunk(
    'assets/addNewAsset',
    async (newAsset, { getState }) => await assetService.create(newAsset, getState().accounts.activeUser.s3_prefix)
);

export const duplicateAsset = createAsyncThunk(
    'assets/duplicateAsset',
    async (existingAssetId, { getState }) =>
        await assetService.duplicate(existingAssetId, getState().accounts.activeUser.s3_prefix)
);

export const publishArray = createAsyncThunk('assets/publishArray', async ({ assetIds, isDraft }) => {
    const response = await assetService.publishArray(assetIds, isDraft);
    response.ids = assetIds;
    return response;
});

export const deleteArray = createAsyncThunk('assets/deleteArray', async (assetIds) => {
    const response = await assetService.deleteArray(assetIds);
    response.ids = assetIds;
    return response;
});

export const pinAsset = createAsyncThunk('assets/pinAsset', async (pinId, { getState }) => {
    const asset = getState().assets.allAssets.find((asset) => asset.asset_id === pinId);
    const params = { object_id: pinId, is_pinned: !asset?.is_pinned };
    const response = await assetService.assetPinning(params);
    response.pinId = pinId;
    response.isPinned = !asset?.is_pinned;
    return response;
});

export const exportAsset = createAsyncThunk('assets/exportAsset', async (params, { getState }) => {
    const response = await assetService.exportAsset(params, getState().accounts.activeUser.s3_prefix);
    return response;
});

export const importAsset = createAsyncThunk(
    'assets/importAsset',
    async (params, { getState }) => await assetService.importAsset(params, getState().accounts.activeUser.s3_prefix)
);

export const copyAssetToClient = createAsyncThunk('assets/AssetCopyToClient', async (params) => {
    return await assetService.copyToClient(params);
});

const setAssetsReloadState = (state) => {
    state.status = LoadingStatus.Idle;
    state.allAssets = [];
    state.loadedRowsMap = {};
};

const assetsTableSlice = createSlice({
    name: 'assets',
    initialState,
    reducers: {
        clearMoreAssets: (state) => {
            state.moreAssets = [];
        },
        clearDisplayZone: (state) => {
            state.displayZone = { items: [], allItems: [] };
        },
        setAssetsStatus: (state, action) => {
            state.status = action.payload;
        },
        resetAssetsTableState: (state) => {
            return initialState;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchAssetsPage.pending, (state) => {
                //state.assetPageInfo = {};
                state.status = LoadingStatus.Loading;
            })
            .addCase(fetchAssetsPage.fulfilled, (state, action) => {
                state.status = LoadingStatus.Loaded;
                state.assetPageInfo = action.payload.pageInfo;
                state.assets = action.payload.assets;
                state.allAssets = action.payload.assets;
                const startIndex = action.meta.arg.page;
                const stopIndex = startIndex + state.assets.length - 1;
                const requestedStopIndex = startIndex + action.meta.arg.pageSize - 1;

                state.loadedRowsMap = {};

                for (var i = startIndex; i <= stopIndex; i++) {
                    state.loadedRowsMap[i] = LoadingStatus.Loaded;
                }

                // In case we didn't load as many as requested
                for (var j = stopIndex + 1; j <= requestedStopIndex; j++) {
                    delete state.loadedRowsMap[j];
                }
            })
            .addCase(fetchAssetsPage.rejected, (state, action) => {
                state.status = LoadingStatus.Failed;
                state.error = action.error.message;
            })
            .addCase(fetchAllAssets.fulfilled, (state, action) => {
                state.status = LoadingStatus.Loaded;
                state.assets = action.payload.assets;
            })
            .addCase(fetchAllAssets.rejected, (state, action) => {
                state.status = LoadingStatus.Failed;
                state.error = action.error.message;
            })
            .addCase(fetchMoreAssets.pending, (state, action) => {
                const startIndex = action.meta.arg.startIndex;
                const stopIndex = action.meta.arg.stopIndex;

                for (var i = startIndex; i <= stopIndex; i++) {
                    state.loadedRowsMap[i] = LoadingStatus.Loading;
                }

                //state.assetPageInfo = {};
                state.status = LoadingStatus.Loading;
            })
            .addCase(fetchMoreAssets.fulfilled, (state, action) => {
                state.status = LoadingStatus.Loaded;

                const categoryArg = action.meta.arg.assetCategoryId;
                const hasCategoryFilter = Array.isArray(categoryArg)
                    ? categoryArg.length > 0
                    : categoryArg || categoryArg === 0;

                // Category-filtered fetches manage their own local asset collection and must
                // not touch the global allAssets/loadedRowsMap bookkeeping used by the
                // unfiltered list, or the two can corrupt each other's pagination state.
                if (hasCategoryFilter) {
                    return;
                }

                const startIndex = action.meta.arg.startIndex;
                const stopIndex = startIndex + action.payload.assets.length - 1;

                const requestedStopIndex = action.meta.arg.stopIndex;

                for (let i = startIndex; i <= stopIndex; i++) {
                    state.loadedRowsMap[i] = LoadingStatus.Loaded;
                }

                for (let i = stopIndex + 1; i <= requestedStopIndex; i++) {
                    delete state.loadedRowsMap[i];
                }

                state.allAssets = state.allAssets.concat(action.payload.assets);
            })
            .addCase(fetchMoreAssets.rejected, (state, action) => {
                const startIndex = action.meta.arg.startIndex;
                const stopIndex = action.meta.arg.stopIndex;

                for (var i = startIndex; i <= stopIndex; i++) {
                    delete state.loadedRowsMap[i];
                }

                state.status = LoadingStatus.Failed;
                state.error = action.error.message;
            })
            .addCase(addNewAsset.rejected, (state, action) => {})
            .addCase(addNewAsset.fulfilled, (state, action) => {
                setAssetsReloadState(state);
            })
            .addCase(duplicateAsset.pending, (state, action) => {
                state.status = LoadingStatus.Loading;
            })
            .addCase(duplicateAsset.fulfilled, (state, action) => {
                setAssetsReloadState(state);
            })
            .addCase(duplicateAsset.rejected, (state, action) => {
                state.status = SavingStatus.Failed;
                state.error = action.error.message;
            })
            .addCase(editAsset.fulfilled, (state, action) => {
                setAssetsReloadState(state);
            })
            .addCase(pinAsset.fulfilled, (state, action) => {
                setAssetsReloadState(state);
            })
            .addCase(pinAsset.rejected, (state, action) => {
                return action.payload;
            })
            .addCase(importAsset.fulfilled, (state, action) => {
                setAssetsReloadState(state);
            })
            .addCase(importAsset.rejected, (state, action) => {})
            .addCase(exportAsset.fulfilled, (state, action) => {
                saveAs(
                    new Blob([JSON.stringify(action.payload)], {
                        type: 'application/json',
                    }),
                    `asset${action.meta.arg.asset_id}.json`
                );
            })
            .addCase(exportAsset.rejected, (state, action) => {})
            .addCase(deleteAsset.fulfilled, (state, action) => {
                setAssetsReloadState(state);
            })
            .addCase(publishById.fulfilled, (state, action) => {
                setAssetsReloadState(state);
            })
            .addCase(publishById.rejected, (state, action) => {
                state.status = SavingStatus.Failed;
                state.error = action.error.message;
            })
            .addCase(publishArray.fulfilled, (state, action) => {
                setAssetsReloadState(state);
            })
            .addCase(deleteArray.fulfilled, (state, action) => {
                setAssetsReloadState(state);
            })
            .addCase(copyAssetToClient.pending, (state, action) => {
                state.status = LoadingStatus.Loading;
            })
            .addCase(copyAssetToClient.fulfilled, (state, action) => {
                setAssetsReloadState(state);
            })
            .addCase(copyAssetToClient.rejected, (state, action) => {
                state.status = SavingStatus.Failed;
                state.error = action.error.message;
            });
    },
});

export const selectAssetPageInfo = (state) => state.assets.assetPageInfo;

export const selectAssetsLoadingStatus = (state) => state.assets.status;

export const selectAssetPage = (state) => state.assets.assets;

export const selectAllAssets = (state) => state.assets.allAssets;

export const selectAssetById = (state, assetId) => state.assets.assets.find((assets) => assets.id === assetId);

export const { clearMoreAssets, resetAssetsTableState, setAssetsStatus } = assetsTableSlice.actions;

export default assetsTableSlice.reducer;
